/**
 * Formatierter E-Mail-Text: gespeichert wird ein kleines Markdown, damit Vorlagen lesbar bleiben und Platzhalter wie
 * {action.date_de} unverändert funktionieren. Zeilen mit „- “ (auch „· “, „• “) sind Aufzählungen, „1. “ nummerierte
 * Listen; **fett**, *kursiv*, __unterstrichen__; „\“ hebt ein Zeichen auf. Jede Zeile wird ein eigener Absatz (div),
 * eine Leerzeile ein leerer Absatz, wie im Editor.
 */

export type RichLine = { kind: 'p' | 'ul' | 'ol'; text: string };

const BULLET = /^\s*[-·•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** Zeilen mit Listenart; Text bleibt im Inline-Format */
export function richLines(md: string): RichLine[] {
  return md.replace(/\r\n?/g, '\n').split('\n').map((line) => {
    const b = BULLET.exec(line);
    if (b) return { kind: 'ul', text: b[1]! };
    const n = NUMBERED.exec(line);
    if (n) return { kind: 'ol', text: n[1]! };
    return { kind: 'p', text: line };
  });
}

const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

type Tok = { t: 'text'; v: string } | { t: 'mark'; v: '**' | '*' | '__' };

function tokenize(s: string): Tok[] {
  const out: Tok[] = [];
  let buf = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === '\\' && i + 1 < s.length) {
      buf += s[++i];
      continue;
    }
    const two = s.slice(i, i + 2);
    if (two === '**' || two === '__') {
      if (buf) out.push({ t: 'text', v: buf });
      buf = '';
      out.push({ t: 'mark', v: two });
      i++;
      continue;
    }
    if (c === '*') {
      if (buf) out.push({ t: 'text', v: buf });
      buf = '';
      out.push({ t: 'mark', v: '*' });
      continue;
    }
    buf += c;
  }
  if (buf) out.push({ t: 'text', v: buf });
  return out;
}

const TAGS = { '**': 'b', '*': 'i', __: 'u' } as const;

/** Inline-Markdown einer Zeile als HTML oder reiner Text; Markierungen ohne Gegenstück bleiben als Zeichen stehen */
function renderInline(s: string, html: boolean): string {
  const toks = tokenize(s);
  // Paare suchen: eine Markierung gilt nur, wenn sie später noch einmal vorkommt
  const paired = new Set<number>();
  const open: { v: string; i: number }[] = [];
  toks.forEach((tk, i) => {
    if (tk.t !== 'mark') return;
    let j = -1;
    for (let k = open.length - 1; k >= 0; k--) if (open[k]!.v === tk.v) (j = k), (k = -1);
    if (j >= 0) {
      paired.add(open[j]!.i).add(i);
      open.splice(j, 1);
    } else open.push({ v: tk.v, i });
  });
  // Umschalten statt Verschachteln: vor jedem Text die offenen Tags an die aktiven Markierungen angleichen (feste
  // Reihenfolge b, i, u), so bleibt das HTML auch bei überlappenden Markierungen gültig
  const active = new Set<string>();
  let out = '';
  let openTags: string[] = [];
  const sync = (want: string[]) => {
    let k = 0;
    while (k < openTags.length && openTags[k] === want[k]) k++;
    out += openTags.slice(k).reverse().map((t) => `</${t}>`).join('') + want.slice(k).map((t) => `<${t}>`).join('');
    openTags = want;
  };
  toks.forEach((tk, i) => {
    if (tk.t === 'text' || !paired.has(i)) {
      if (html) sync((['b', 'i', 'u'] as const).filter((t) => active.has(t)));
      out += html ? escHtml(tk.v) : tk.v;
      return;
    }
    const tag = TAGS[tk.v];
    if (active.has(tag)) active.delete(tag);
    else active.add(tag);
  });
  if (html) sync([]);
  return out;
}

/** HTML für den Editor und den Versand: ein div je Zeile, Listen als ul/ol */
export function richToHtml(md: string, opts: { email?: boolean } = {}): string {
  const lines = richLines(md);
  const listStyle = opts.email ? ' style="margin:0;padding-left:1.6em"' : '';
  let out = '';
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]!;
    if (l.kind === 'p') {
      const inner = renderInline(l.text, true);
      out += `<div>${inner || '<br>'}</div>`;
      continue;
    }
    const tag = l.kind;
    out += `<${tag}${listStyle}>`;
    while (i < lines.length && lines[i]!.kind === tag) {
      out += `<li>${renderInline(lines[i]!.text, true) || '<br>'}</li>`;
      i++;
    }
    i--;
    out += `</${tag}>`;
  }
  if (opts.email) return `<div style="font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.4">${out}</div>`;
  return out;
}

/** Reiner Text (mailto, Text-Teil der Mail, Kurzansicht): Markierungen weg, Aufzählungen mit „•“ */
export function richToText(md: string): string {
  let n = 0;
  return richLines(md)
    .map((l) => {
      const t = renderInline(l.text, false);
      if (l.kind === 'ol') return `${++n}. ${t}`;
      n = 0;
      return l.kind === 'ul' ? `• ${t}` : t;
    })
    .join('\n');
}

/** Text so maskieren, dass er wörtlich erscheint (Platzhalterwerte, Signatur); Platzhalter selbst bleiben unberührt */
export function escapeRich(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\*/g, '\\*')
    .replace(/__/g, '\\_\\_')
    .replace(/^(\s*)([-·•])(\s)/gm, '$1\\$2$3')
    .replace(/^(\s*\d+)([.)])(\s)/gm, '$1\\$2$3');
}

/** Ein Textstück mit Formatierung (für die Umwandlung aus dem Editor) */
export type RichRun = { text: string; b?: boolean; i?: boolean; u?: boolean };

/** Zeilen aus dem Editor (Runs mit Formatierung) zurück ins gespeicherte Format */
export function runsToRich(lines: { kind: RichLine['kind']; runs: RichRun[] }[]): string {
  let n = 0;
  return lines
    .map((l) => {
      // gleiche Formatierung zusammenfassen
      const runs: RichRun[] = [];
      for (const r of l.runs) {
        if (!r.text) continue;
        const last = runs[runs.length - 1];
        if (last && !!last.b === !!r.b && !!last.i === !!r.i && !!last.u === !!r.u) last.text += r.text;
        else runs.push({ ...r });
      }
      let text = runs
        .map((r) => {
          const lit = r.text.replace(/\\/g, '\\\\').replace(/\*/g, '\\*').replace(/__/g, '\\_\\_');
          if (!r.b && !r.i && !r.u) return lit;
          // Leerzeichen außerhalb der Markierungen, sonst greift sie nicht sauber
          const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(lit)!;
          if (!m[2]) return lit;
          const open = (r.u ? '__' : '') + (r.b ? '**' : '') + (r.i ? '*' : '');
          const close = (r.i ? '*' : '') + (r.b ? '**' : '') + (r.u ? '__' : '');
          return m[1] + open + m[2] + close + m[3];
        })
        .join('');
      if (l.kind === 'p') {
        n = 0;
        text = text.replace(/^(\s*)([-·•])(\s)/, '$1\\$2$3').replace(/^(\s*\d+)([.)])(\s)/, '$1\\$2$3');
        return text;
      }
      if (l.kind === 'ol') return `${++n}. ${text.trim()}`;
      n = 0;
      return `- ${text.trim()}`;
    })
    .join('\n');
}
